/**
 * Resume template settings — the adviser's information that appears on EVERY resume
 * (logo, company name, address, contact line, colour, "presented by" footer) plus the
 * layout defaults (section order, candidate-contact visibility, references line).
 *
 * By default the letterhead comes from the firm profile the agreements already use
 * (Agreements → Setup), so the logo/address are maintained in one place; anything
 * typed here overrides it for resumes only.
 *
 * Backend: GET/PUT /resumes/template, POST /resumes/template/preview (sample PDF).
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import useAuthenticatedAxios from '../../../../auth/AuthenticatedAxios';
import { useToast } from '../../../../Context/ToastContext';
import {
  Overlay, Shell, TopBar, Title, Spacer, Body, EditorPane, PreviewPane, Iframe, PreviewEmpty,
  Row, Field, Label, Hint, Input, TextArea, Btn, IconBtn, Check, HiddenFile, FileLabel, Banner,
} from './resumeStyles';
import { Section } from './ResumeSectionEditors';

const SECTION_LABELS = {
  summary: 'Professional Summary',
  skills: 'Skills',
  experience: 'Professional Experience',
  education: 'Education',
  certifications: 'Certifications & Licences',
  languages: 'Languages',
  volunteer: 'Volunteer Experience',
  additional: 'Custom sections',
};

const ACCENTS = ['#1F4E79', '#0F766E', '#8B1E3F', '#334155', '#7C3AED', '#B45309'];

const base64ToBlobUrl = (b64) => {
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
};

export default function ResumeTemplateSettings({ onClose, onSaved }) {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [firm, setFirm] = useState(null);
  const [logoChanged, setLogoChanged] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/resumes/template');
        const s = r.data.data.settings;
        setForm({ ...s, addressLines: (s.addressLines || []).join('\n') });
        setFirm(r.data.data.firmProfile);
      } catch (e) {
        showToast('Could not load resume settings', 'error');
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const payload = () => {
    const p = { ...form, addressLines: String(form.addressLines || '').split('\n').map(s => s.trim()).filter(Boolean) };
    delete p._id; delete p.expertId; delete p.createdAt; delete p.updatedAt; delete p.__v;
    if (!logoChanged) delete p.logoDataUrl;     // don't resend an unchanged (possibly large) logo
    return p;
  };

  const doPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post('/resumes/template/preview', payload());
      setPreview(prev => { if (prev) URL.revokeObjectURL(prev); return base64ToBlobUrl(r.data.data.pdfBase64); });
    } catch (e) { showToast(e.response?.data?.message || 'Preview failed', 'error'); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put('/resumes/template', payload());
      const s = r.data.data.settings;
      setForm({ ...s, addressLines: (s.addressLines || []).join('\n') });
      setLogoChanged(false);
      showToast('Resume template saved — applies to every resume', 'success');
      onSaved && onSaved();
    } catch (e) { showToast(e.response?.data?.message || 'Failed to save', 'error'); }
    finally { setBusy(false); }
  };

  const moveSection = (i, dir) => {
    const order = [...(form.sectionOrder || [])];
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    set({ sectionOrder: order });
  };

  const fallback = (v) => (form?.useFirmProfile && v ? `From firm profile: ${v}` : '');

  return createPortal(
    <Overlay style={{ zIndex: 5100 }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <Shell style={{ maxWidth: 1300 }}>
        <TopBar>
          <Title>Resume template — on every resume</Title>
          <Spacer />
          <Btn $variant="ghost" onClick={doPreview} disabled={busy || !form}>Preview sample</Btn>
          <Btn onClick={save} disabled={busy || !form}>Save template</Btn>
          <IconBtn title="Close" onClick={onClose}>×</IconBtn>
        </TopBar>
        <Body>
          <EditorPane>
            {loading || !form ? <Hint>Loading…</Hint> : (
              <>
                <Section title="Company letterhead">
                  <Check style={{ marginBottom: 10 }}>
                    <input type="checkbox" checked={!!form.useFirmProfile} onChange={e => set({ useFirmProfile: e.target.checked })} />
                    Use my firm profile (logo, name, address, phone, email from Agreements → Setup) for blank fields
                  </Check>
                  {form.useFirmProfile && firm && (
                    <Banner>
                      Firm profile: <b>{firm.firmName || '—'}</b>{firm.addressLines?.length ? ` · ${firm.addressLines.join(', ')}` : ''}
                      {firm.phone ? ` · ${firm.phone}` : ''}{firm.email ? ` · ${firm.email}` : ''} · logo {firm.hasLogo ? 'on file' : 'not set'}
                    </Banner>
                  )}
                  <Row>
                    <Field><Label>Company name</Label><Input value={form.companyName || ''} placeholder={fallback(firm?.firmName)} onChange={e => set({ companyName: e.target.value })} /></Field>
                    <Field><Label>Website</Label><Input value={form.website || ''} onChange={e => set({ website: e.target.value })} /></Field>
                  </Row>
                  <Row $mt>
                    <Field><Label>Address (one line each)</Label>
                      <TextArea $h={64} value={form.addressLines || ''} placeholder={fallback(firm?.addressLines?.join(' / '))} onChange={e => set({ addressLines: e.target.value })} />
                    </Field>
                  </Row>
                  <Row $mt>
                    <Field><Label>Phone</Label><Input value={form.phone || ''} placeholder={fallback(firm?.phone)} onChange={e => set({ phone: e.target.value })} /></Field>
                    <Field><Label>Email</Label><Input value={form.email || ''} placeholder={fallback(firm?.email)} onChange={e => set({ email: e.target.value })} /></Field>
                  </Row>
                  <Row $mt style={{ alignItems: 'center' }}>
                    <HiddenFile id="resume-logo-upload" type="file" accept="image/png,image/jpeg" onChange={e => {
                      const f = e.target.files?.[0]; if (!f) return;
                      if (f.size > 2 * 1024 * 1024) { showToast('Logo must be under 2 MB', 'warning'); return; }
                      const r = new FileReader();
                      r.onload = () => { set({ logoDataUrl: r.result }); setLogoChanged(true); };
                      r.readAsDataURL(f);
                    }} />
                    <FileLabel htmlFor="resume-logo-upload">{form.logoDataUrl ? 'Replace resume logo…' : 'Upload a resume-specific logo…'}</FileLabel>
                    {form.logoDataUrl && <>
                      <img src={form.logoDataUrl} alt="logo" style={{ height: 34, background: '#fff', borderRadius: 4, padding: 2 }} />
                      <Btn $sm $variant="ghost" onClick={() => { set({ logoDataUrl: '' }); setLogoChanged(true); }}>Remove</Btn>
                    </>}
                    {!form.logoDataUrl && <Hint>{form.useFirmProfile && firm?.hasLogo ? 'Using the firm-profile logo.' : 'No logo.'}</Hint>}
                  </Row>
                  <Row $mt>
                    <Check><input type="checkbox" checked={!!form.showCompanyHeader} onChange={e => set({ showCompanyHeader: e.target.checked })} />Show the company letterhead at the top of page 1</Check>
                  </Row>
                </Section>

                <Section title="Look & wording">
                  <Label>Accent colour (name + section headings)</Label>
                  <Row style={{ alignItems: 'center', marginTop: 6 }}>
                    {ACCENTS.map(c => (
                      <button key={c} type="button" title={c} onClick={() => set({ accentColor: c })}
                        style={{ width: 26, height: 26, borderRadius: 6, background: c, cursor: 'pointer',
                          border: form.accentColor?.toLowerCase() === c.toLowerCase() ? '2px solid #fff' : '1px solid rgba(255,255,255,0.2)' }} />
                    ))}
                    <input type="color" value={form.accentColor || '#1F4E79'} onChange={e => set({ accentColor: e.target.value.toUpperCase() })}
                      style={{ width: 36, height: 28, background: 'none', border: 'none', cursor: 'pointer' }} />
                  </Row>
                  <Row $mt>
                    <Field><Label>Footer “presented by” line (use {'{{companyName}}'})</Label>
                      <Input value={form.presentedByText || ''} onChange={e => set({ presentedByText: e.target.value })} /></Field>
                  </Row>
                  <Row $mt>
                    <Field><Label>Footer note (optional, e.g. confidentiality)</Label>
                      <Input value={form.footerNote || ''} placeholder="Confidential — provided by {{companyName}} for recruitment purposes only." onChange={e => set({ footerNote: e.target.value })} /></Field>
                  </Row>
                  <Row $mt style={{ alignItems: 'center' }}>
                    <Check><input type="checkbox" checked={!!form.includeReferencesLine} onChange={e => set({ includeReferencesLine: e.target.checked })} />End with a references line</Check>
                    <Field $grow={2}><Input value={form.referencesText || ''} disabled={!form.includeReferencesLine} onChange={e => set({ referencesText: e.target.value })} /></Field>
                  </Row>
                </Section>

                <Section title="Candidate contact details">
                  <Check>
                    <input type="checkbox" checked={!!form.showCandidateContact} onChange={e => set({ showCandidateContact: e.target.checked })} />
                    Show the candidate's phone, email and LinkedIn by default
                  </Check>
                  <Hint style={{ marginTop: 8 }}>
                    Turn this off if you submit candidates to employers yourself: the resume then says
                    “Contact through: {form.companyName || firm?.firmName || 'your company'}” so employers reach you, not the candidate directly.
                    Each resume can override this.
                  </Hint>
                </Section>

                <Section title="Section order">
                  {(form.sectionOrder || []).map((k, i) => (
                    <Row key={k} style={{ alignItems: 'center', marginBottom: 6, flexWrap: 'nowrap' }}>
                      <span style={{ width: 22, color: '#8b93a7', fontSize: 12 }}>{i + 1}.</span>
                      <span style={{ flex: 1, fontSize: 13.5 }}>{SECTION_LABELS[k] || k}</span>
                      <IconBtn title="Move up" disabled={i === 0} onClick={() => moveSection(i, -1)}>↑</IconBtn>
                      <IconBtn title="Move down" disabled={i === form.sectionOrder.length - 1} onClick={() => moveSection(i, 1)}>↓</IconBtn>
                    </Row>
                  ))}
                  <Hint style={{ marginTop: 6 }}>Empty sections are skipped automatically. Standard Canadian order: summary, skills, experience, education.</Hint>
                </Section>
              </>
            )}
          </EditorPane>
          <PreviewPane>
            {preview ? <Iframe title="Resume template preview" src={preview} /> : (
              <PreviewEmpty>Click “Preview sample” to see your letterhead and layout on a sample resume.</PreviewEmpty>
            )}
          </PreviewPane>
        </Body>
      </Shell>
    </Overlay>,
    document.body
  );
}
